// Shared category model + transforms.
//
// The storefront historically read a hard-coded CATEGORIES array and two
// translation maps. Categories now live in Firestore so admins can edit them,
// but the static data is kept as the seed + offline fallback. Everything in the
// app consumes the *normalized* shape produced here:
//
//   { id, title, slug, link, order, subCategories: [{ title, slug, link, order }] }
//
// where `slug` is the URL token stored on products (e.g. "weapons", "rifles")
// and `link` is "/" + slug (Navbar builds child links as category.link + sub.link).

import CATEGORIES from "./categories";
import {
  CATEGORIES_TRANSLATION,
  SUB_CATEGORIES_TRANSLATION,
} from "../translations/translations";

const stripSlash = (link) => (link || "").replace(/^\//, "");

// --- Static (fallback / seed) ---------------------------------------------
export const staticCategories = CATEGORIES.map((cat, i) => ({
  id: stripSlash(cat.link),
  title: cat.title,
  slug: stripSlash(cat.link),
  link: cat.link,
  order: i,
  subCategories: (cat.subCategories || []).map((sub, j) => ({
    title: sub.title,
    slug: stripSlash(sub.link),
    link: sub.link,
    order: j,
  })),
}));

// Build { slug: title } lookups from a normalized tree.
export const buildTranslations = (tree) => {
  const catTranslation = {};
  const subTranslation = {};
  tree.forEach((cat) => {
    catTranslation[cat.slug] = cat.title;
    (cat.subCategories || []).forEach((sub) => {
      subTranslation[sub.slug] = sub.title;
    });
  });
  return { catTranslation, subTranslation };
};

// --- Firestore <-> model ---------------------------------------------------
export const firestoreDocToCategory = (id, data) => ({
  id,
  title: data.title || "",
  slug: data.slug || "",
  link: "/" + (data.slug || ""),
  order: typeof data.order === "number" ? data.order : 0,
  subCategories: (data.subCategories || [])
    .map((sub, i) => ({
      title: sub.title || "",
      slug: sub.slug || "",
      link: "/" + (sub.slug || ""),
      order: typeof sub.order === "number" ? sub.order : i,
    }))
    .sort((a, b) => a.order - b.order),
});

export const sortTree = (tree) =>
  [...tree].sort((a, b) => a.order - b.order);

// Strip the derived `link` fields before writing to Firestore.
export const categoryToFirestore = (cat) => ({
  title: cat.title || "",
  slug: cat.slug || "",
  order: typeof cat.order === "number" ? cat.order : 0,
  subCategories: (cat.subCategories || []).map((sub, i) => ({
    title: sub.title || "",
    slug: sub.slug || "",
    order: typeof sub.order === "number" ? sub.order : i,
  })),
});

// The translation maps the app starts with: the static maps as a base, with the
// static category tree overlaid. Firestore data overlays this once it loads, so
// labels for slugs no longer present in the tree (but still on old products)
// keep resolving.
export const baseCatTranslation = {
  ...CATEGORIES_TRANSLATION,
  ...buildTranslations(staticCategories).catTranslation,
};

export const baseSubTranslation = {
  ...SUB_CATEGORIES_TRANSLATION,
  ...buildTranslations(staticCategories).subTranslation,
};
